import React, { useState } from "react";
import { useItemsFilter } from "../apis/queries";
import moment from "moment";
import { ApiEndPoint } from "../constants/constant";
import { selectedUserAtom } from "../config/globalStates";
import { useAtom } from "jotai";
import CuScreenLoader from "../components/CuScreenLoader";
import CuStatusMessage from "../layouts/CuStatusMessage";
import { cufrontIcons } from "../assets/icons/icons";
import VisitPrescriptionSm from "../layouts/VisitPrescriptionSm";
import {
    FaChevronDown,
    FaChevronUp,
    FaFileInvoiceDollar,
    FaFilePrescription,
} from "react-icons/fa";
import { TbCertificate } from "react-icons/tb";
import ImagePreview from "../components/ImagePreview";

import CuModal from "../layouts/CuModal";
import InvoiceComponent from "../components/InvoiceComponent";

const Visits = () => {
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState();
    const [selectedUser] = useAtom(selectedUserAtom);
    const [expandedDates, setExpandedDates] = useState({});
    const [filterType, setFilterType] = useState("All");

    // Bill/Invoice State
    const [showInvoice, setShowInvoice] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    // Image Preview State
    const [openPdfViewer, setOpenPdfViewer] = useState(false);
    const [pdfData, setPdfData] = useState();
    const [mainImage, setMainImage] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Fetch visits data using getVisits action
    const { data: visitsResponse, isLoading } = useItemsFilter(
        `${ApiEndPoint.USERS}/`,
        `?action=getVisits&frontend=True&clinic_id=1&patient_id=${selectedUser?.id}`,
        selectedUser?.id ? true : false
    );

    const rawVisitsData = visitsResponse || {};

    // Filter Logic
    const getFilteredData = () => {
        if (filterType === "All") return rawVisitsData;

        const filtered = {};
        Object.keys(rawVisitsData).forEach((date) => {
            const records = rawVisitsData[date].filter((item) => {
                if (filterType === "Prescriptions")
                    return item.type === "Prescription";
                if (filterType === "Bills") return item.type === "Bill";
                if (filterType === "Certificates")
                    return item.type === "Certificate";
                return true;
            });
            if (records.length > 0) {
                filtered[date] = records;
            }
        });
        return filtered;
    };

    const visitsData = getFilteredData();
    const dates = Object.keys(visitsData).sort(
        (a, b) => new Date(b) - new Date(a)
    );

    const { data: consultationData, isLoading: dataLoading } = useItemsFilter(
        ApiEndPoint.QP_CM_CONSULTATION_DATA,
        `?action=getConsultationData&frontend=True&clinic_id=1&id=${modalData?.id}&settings=true`,
        modalData?.id && modalData?.type === "Prescription" ? true : false
    );

    const handleOpenPdfViewer = (item) => {
        setPdfData(item);
        setOpenPdfViewer(true);
    };

    const handleRecordClick = (item) => {
        if (item.type === "Prescription") {
            setModalData(item);
            setShowModal(true);
        } else if (item.type === "Bill") {
            setSelectedInvoice(item.raw_data);
            setShowInvoice(true);
        } else if (item.type === "Certificate") {
            handleOpenPdfViewer(item);
        } else if (item.pdf_url) {
            window.open(item.pdf_url, "_blank");
        }
    };

    const handleCloseInvoice = () => {
        setShowInvoice(false);
        setSelectedInvoice(null);
    };

    const toggleDate = (date) => {
        setExpandedDates((prev) => ({
            ...prev,
            [date]: !prev[date],
        }));
    };

    React.useEffect(() => {
        if (dates.length > 0) {
            const initialExpanded = {};
            dates.forEach((date) => {
                initialExpanded[date] = true;
            });
            setExpandedDates(initialExpanded);
        }
    }, [visitsResponse, filterType]);

    const getIcon = (type) => {
        switch (type) {
            case "Prescription":
                return (
                    <FaFilePrescription size={20} className="text-green-600" />
                );
            case "Bill":
                return (
                    <FaFileInvoiceDollar size={20} className="text-green-600" />
                );
            case "Certificate":
                return <TbCertificate size={22} className="text-green-600" />;
            default:
                return (
                    <FaFilePrescription size={20} className="text-green-600" />
                );
        }
    };

    const filters = ["All", "Prescriptions", "Bills", "Certificates"];

    return (
        <>
            {isLoading ? (
                <CuScreenLoader />
            ) : (
                <div className="h-full p-4 relative flex flex-col">
                    {/* Filter Tabs */}
                    <div className="flex gap-2 overflow-x-auto pb-4 mb-2 no-scrollbar">
                        {filters.map((filter) => (
                            <button
                                key={filter}
                                onClick={() => setFilterType(filter)}
                                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                                    filterType === filter
                                        ? "bg-green-100 text-green-700 border border-green-200"
                                        : "bg-gray-50 text-gray-600 border border-gray-100 hover:bg-gray-100"
                                }`}
                            >
                                {filter}
                            </button>
                        ))}
                    </div>

                    {dates.length > 0 ? (
                        <>
                            <div className="absolute left-[29px] top-[70px] bottom-0 w-[2px] bg-gray-200 z-0"></div>
                            {dates.map((date, index) => (
                                <div
                                    key={date}
                                    className="mb-6 relative z-10 pl-8"
                                >
                                    <div className="absolute left-[21px] top-[14px] w-4 h-4 rounded-full border-[3px] border-white bg-green-500 shadow-sm"></div>
                                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                                        <div
                                            className="flex justify-between items-center p-4 cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                                            onClick={() => toggleDate(date)}
                                        >
                                            <h3 className="font-semibold text-gray-700">
                                                {moment(date).format(
                                                    "DD MMMM YYYY"
                                                )}
                                            </h3>
                                            <div className="text-gray-500">
                                                {expandedDates[date] ? (
                                                    <FaChevronUp />
                                                ) : (
                                                    <FaChevronDown />
                                                )}
                                            </div>
                                        </div>
                                        {expandedDates[date] && (
                                            <div className="border-t border-gray-100">
                                                {visitsData[date].map(
                                                    (item, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="flex items-center p-4 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 border-gray-100 transition-colors"
                                                            onClick={() =>
                                                                handleRecordClick(
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            <div className="p-2 bg-green-50 rounded-lg mr-4">
                                                                {getIcon(
                                                                    item.type
                                                                )}
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex justify-between items-start">
                                                                    <h4 className="font-medium text-gray-800 text-sm md:text-base">
                                                                        {
                                                                            item.type
                                                                        }
                                                                    </h4>
                                                                    {item.amount && (
                                                                        <span className="text-xs font-semibold px-2 py-1 bg-green-100 text-green-700 rounded-full">
                                                                            ₹
                                                                            {
                                                                                item.amount
                                                                            }
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <p className="text-xs md:text-sm text-gray-500 mt-1">
                                                                    {item.type ===
                                                                    "Prescription"
                                                                        ? item.doctor_name
                                                                        : item.type ===
                                                                            "Bill"
                                                                          ? item.description
                                                                          : item.name}
                                                                </p>
                                                            </div>
                                                            <div className="ml-2">
                                                                <img
                                                                    src={
                                                                        cufrontIcons.rightArrow
                                                                    }
                                                                    alt="arrow"
                                                                    className="w-4 h-4 opacity-40"
                                                                />
                                                            </div>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </>
                    ) : (
                        <CuStatusMessage
                            heading={"You don’t have any visit record"}
                            img={cufrontIcons?.NoAppointment}
                        />
                    )}

                    {!dataLoading && showModal && (
                        <VisitPrescriptionSm
                            setShowModal={setShowModal}
                            consultationData={consultationData}
                        />
                    )}

                    {showInvoice && selectedInvoice && (
                        <CuModal closeModal={handleCloseInvoice}>
                            <div className="w-[90vw] max-w-[480px] max-h-[85vh] overflow-auto bg-white rounded-lg shadow-xl">
                                <InvoiceComponent
                                    closeInvoice={handleCloseInvoice}
                                    data={selectedInvoice}
                                />
                            </div>
                        </CuModal>
                    )}

                    {openPdfViewer && (
                        <ImagePreview
                            uploadedFile={
                                pdfData?.files?.flat().length > 0
                                    ? pdfData.files.flat()
                                    : [
                                          {
                                              filename: pdfData?.files_id,
                                              url: pdfData?.files_id,
                                          },
                                      ]
                            }
                            mainImage={mainImage}
                            setMainImage={setMainImage}
                            setShowPreview={setOpenPdfViewer}
                            setSelectedIndex={setSelectedIndex}
                            selectedIndex={selectedIndex}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default Visits;
